import {
  Sidebar,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarScrollWrapper,
  SidebarSpace,
  SidebarSubmenu,
  SidebarSubmenuItem,
} from '@backstage/core-components';
import { NavContentBlueprint } from '@backstage/plugin-app-react';
import { NotificationsSidebarItem } from '@backstage/plugin-notifications';
import { SidebarSearchModal } from '@backstage/plugin-search';
import { UserSettingsSignInAvatar } from '@backstage/plugin-user-settings';
import AssessmentIcon from '@material-ui/icons/Assessment';
import BuildIcon from '@material-ui/icons/Build';
import FlightTakeoffIcon from '@material-ui/icons/FlightTakeoff';
import HistoryIcon from '@material-ui/icons/History';
import HomeIcon from '@material-ui/icons/Home';
import MenuBookIcon from '@material-ui/icons/MenuBook';
import MenuIcon from '@material-ui/icons/Menu';
import NotificationsIcon from '@material-ui/icons/Notifications';
import SearchIcon from '@material-ui/icons/Search';
import SecurityIcon from '@material-ui/icons/Security';
import SettingsApplicationsIcon from '@material-ui/icons/SettingsApplications';
import StorageIcon from '@material-ui/icons/Storage';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import { PLATFORM_CAPABILITIES } from '../capabilities/model';
import { SidebarLogo } from './SidebarLogo';

const moduleIcons = {
  assess: AssessmentIcon,
  move: FlightTakeoffIcon,
  adapt: BuildIcon,
  prove: VerifiedUserIcon,
  inventory: StorageIcon,
  guidance: MenuBookIcon,
  history: HistoryIcon,
  notifications: NotificationsIcon,
  assurance: SecurityIcon,
} as const;

const migrateCapability = PLATFORM_CAPABILITIES.find(
  capability => capability.id === 'migrate',
);
const operateCapability = PLATFORM_CAPABILITIES.find(
  capability => capability.id === 'operate',
);

if (!migrateCapability || !operateCapability) {
  throw new Error('MIGRATE and OPERATE navigation definitions are required');
}

const capabilityPageIds = PLATFORM_CAPABILITIES.flatMap(capability => [
  `page:horizon-capabilities/${capability.id}`,
  ...capability.modules
    .filter(module => module.roadmap)
    .map(module => `page:horizon-capabilities/${module.id}`),
]);

export const SidebarContent = NavContentBlueprint.make({
  params: {
    component: ({ navItems }) => {
      const nav = navItems.withComponent(item => (
        <SidebarItem icon={() => item.icon} to={item.href} text={item.title} />
      ));

      // These pages are available through curated navigation or dedicated UI.
      nav.take('page:search');
      nav.take('page:notifications');
      nav.take('page:scaffolder');
      nav.take('page:horizon-move');
      nav.take('page:horizon-assess-results/evaluation');
      nav.take('page:horizon-assess-results/scope');
      capabilityPageIds.forEach(pageId => nav.take(pageId));

      const homeItem = nav.take('page:home');
      const catalogItem = nav.take('page:catalog');
      const appVisualizerItem = nav.take('page:app-visualizer');
      const userSettingsItem = nav.take('page:user-settings');
      const remainingItems = nav.rest({ sortBy: 'title' });

      return (
        <Sidebar>
          <SidebarLogo />
          <SidebarGroup label="Home" icon={<HomeIcon />} to="/">
            {homeItem}
          </SidebarGroup>
          <SidebarGroup label="Capabilities" icon={<MenuIcon />}>
            <SidebarItem
              icon={FlightTakeoffIcon}
              text={migrateCapability.title}
              to={migrateCapability.path}
            >
              <SidebarSubmenu title={migrateCapability.title}>
                {migrateCapability.modules.map(module => (
                  <SidebarSubmenuItem
                    exact
                    icon={moduleIcons[module.icon]}
                    key={module.id}
                    title={module.title}
                    to={module.path}
                  />
                ))}
              </SidebarSubmenu>
            </SidebarItem>
            <SidebarItem
              icon={SettingsApplicationsIcon}
              text={operateCapability.title}
              to={operateCapability.path}
            >
              <SidebarSubmenu title={operateCapability.title}>
                {operateCapability.modules.map(module => (
                  <SidebarSubmenuItem
                    exact
                    icon={moduleIcons[module.icon]}
                    key={module.id}
                    title={module.title}
                    to={module.path}
                  />
                ))}
              </SidebarSubmenu>
            </SidebarItem>
            <SidebarDivider />
            {catalogItem}
            <SidebarScrollWrapper>{remainingItems}</SidebarScrollWrapper>
          </SidebarGroup>
          <SidebarDivider />
          <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
            <SidebarSearchModal />
          </SidebarGroup>
          <SidebarSpace />
          <SidebarDivider />
          <NotificationsSidebarItem />
          <SidebarDivider />
          <SidebarGroup
            label="Settings"
            icon={<UserSettingsSignInAvatar />}
            to="/settings"
          >
            {appVisualizerItem}
            {userSettingsItem}
          </SidebarGroup>
        </Sidebar>
      );
    },
  },
});
